"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import SanitizedHtml from "@/components/SanitizedHtml";
import type { Post } from "@/lib/types";
import { useTranslations } from "@/i18n/LocaleProvider";

// Post.publishedAt is typed as `number | null`, but a document read straight off the Firestore
// SDK actually carries a Timestamp object (see admin/posts/AdminPosts.tsx's serverTimestamp()
// writes) -- accepting either shape here is simpler than threading a conversion through every
// query that touches this field.
function formatDate(value: number | { toMillis(): number } | null): string {
  if (value === null) return "";
  const ms = typeof value === "number" ? value : value.toMillis();
  return new Date(ms).toLocaleDateString("en-IN", { dateStyle: "long" });
}

export default function BlogView() {
  const { t } = useTranslations();
  const slug = useSearchParams().get("post");
  const [posts, setPosts] = useState<Post[]>([]);
  const [post, setPost] = useState<Post | null>(null);
  const [notFound, setNotFound] = useState(false);
  // Starts true, not false -- both fetch paths below are async, and `posts`/`post` starting
  // empty/null is indistinguishable from "genuinely no posts" until this flips. Without it,
  // "Nothing here yet." (or a blank single-post page) flashes for every visitor on every load,
  // not just a slow connection, since Firestore's very first byte still takes a real round trip.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      if (slug) {
        try {
          const snap = await getDoc(doc(db, "posts", slug));
          if (snap.exists() && snap.data().isPublished) {
            setPost({ id: snap.id, ...snap.data() } as Post);
          } else {
            setNotFound(true);
          }
        } catch {
          setNotFound(true);
        } finally {
          setLoading(false);
        }
      } else {
        try {
          const snap = await getDocs(
            query(
              collection(db, "posts"),
              where("isPublished", "==", true),
              orderBy("publishedAt", "desc")
            )
          );
          setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Post));
        } finally {
          setLoading(false);
        }
      }
    }
    void load();
  }, [slug]);

  if (slug) {
    if (notFound) {
      return (
        <main className="mx-auto w-full max-w-4xl px-6 py-10 sm:py-16">
          <p>
            {t("blog.notFound")}{" "}
            <Link href="/blog" className="text-accent underline">
              {t("blog.backToAllPosts")}
            </Link>
          </p>
        </main>
      );
    }
    if (!post) {
      return loading ? (
        <main className="mx-auto w-full max-w-4xl px-6 py-10 sm:py-16">
          <p className="text-sm text-muted">{t("blog.loading")}</p>
        </main>
      ) : null;
    }
    return (
      <main className="mx-auto w-full max-w-4xl px-6 py-10 sm:py-16">
        <Link
          href="/blog"
          className="group inline-flex items-center gap-1.5 text-sm text-muted transition hover:text-accent"
        >
          <span aria-hidden>&larr;</span>
          <span className="group-hover:underline">{t("blog.allPosts")}</span>
        </Link>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl">{post.title}</h1>
        <p className="mt-2 text-sm text-muted">
          Devina Prasad &middot; {formatDate(post.publishedAt)}
        </p>
        {post.coverImageUrl && (
          <div className="surface-card relative mt-6 aspect-[16/9] w-full overflow-hidden">
            <Image src={post.coverImageUrl} alt={post.title} fill sizes="896px" className="object-cover" />
          </div>
        )}
        {/* Not `prose` -- this project never installed @tailwindcss/typography, so that class
            was silently generating zero CSS (see RichTextEditor.tsx for the same bug found and
            fixed on the editing side): every bullet/numbered list in a post's body rendered with
            no marker or indent at all, since only headings/paragraphs/links had explicit
            overrides here. Hand-styled the same way LegalPageLayout/CmsPageView already do. */}
        <SanitizedHtml
          className="mt-8 max-w-none leading-relaxed [&_a]:text-accent [&_a]:underline [&_h2]:mt-8 [&_h2]:font-display [&_h2]:text-2xl [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_blockquote]:border-l-4 [&_blockquote]:border-foreground/20 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-foreground/70"
          html={post.content}
        />
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-4xl px-6 py-10 sm:py-16">
      <h1 className="text-center font-display text-3xl sm:text-4xl">{t("blog.title")}</h1>
      <div className="mt-10 space-y-6">
        {loading && <p className="text-sm text-muted">{t("blog.loading")}</p>}
        {!loading && posts.length === 0 && <p className="text-sm text-muted">{t("blog.empty")}</p>}
        {posts.map((p) => (
          <Link
            key={p.id}
            href={`/blog?post=${p.id}`}
            className="surface-card flex flex-col gap-5 overflow-hidden p-5 transition hover:border-accent/40 sm:flex-row sm:items-center"
          >
            {p.coverImageUrl && (
              <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-xl sm:w-48">
                <Image src={p.coverImageUrl} alt={p.title} fill sizes="192px" className="object-cover" />
              </div>
            )}
            <div>
              <h2 className="font-display text-2xl hover:text-accent">{p.title}</h2>
              <p className="mt-1 text-sm text-muted">
                Devina Prasad &middot; {formatDate(p.publishedAt)}
              </p>
              <p className="mt-2 text-foreground/70">{p.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}
